import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CalendarDays,
  Clock3,
  Coffee,
  Compass,
  Heart,
  Home,
  Instagram,
  MapPin,
  MessageCircle,
  Sparkles,
  Users,
} from "lucide-react";
import { SunLogo } from "../components/SunLogo";
import { LangSwitcher } from "../components/LangSwitcher";
import { IMG } from "../data/images";
import { INFO } from "../data/info";
import { MENU, tr } from "../data/menu";
import { T } from "../i18n";
import "./V2.css";

const FAVORITES = [
  { image: IMG.waffle, price: "5.00 €", ...T.es.highlights.cards[0] },
  { image: IMG.matcha, price: "4.50 €", ...T.es.highlights.cards[1] },
  { image: IMG.crepe, price: "4.90 €", ...T.es.highlights.cards[2] },
  { image: IMG.bagel, price: "6.20 €", ...T.es.highlights.cards[3] },
];

const HOURS = ["9:00", "9:30", "10:00", "10:30", "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"];

const PEOPLE = ["1", "2", "3", "4", "5", "6", "7", "8", "9 o más"];

const todayLocal = () => {
  const date = new Date();
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const MenuRow = ({ item }) => (
  <li className="v2-menu-row">
    <div>
      <strong>{item.n}</strong>
      {item.d && <p>{tr(item.d, "es")}</p>}
    </div>
    <span>{item.p} €</span>
  </li>
);

const MenuPanel = ({ category }) => (
  <div className="v2-menu-panel" key={category.id}>
    {category.note && <p className="v2-menu-note">{tr(category.note, "es")}</p>}
    {category.items && (
      <ul className="v2-menu-list">
        {category.items.map((item) => <MenuRow item={item} key={item.n} />)}
      </ul>
    )}
    {category.groups && (
      <div className="v2-menu-groups">
        {category.groups.map((group) => (
          <section key={group.key}>
            <h3>{tr(group.title, "es")}</h3>
            {group.items && <ul className="v2-menu-list">{group.items.map((item) => <MenuRow item={item} key={item.n} />)}</ul>}
            {group.flat && <ul className="v2-flat-list">{tr(group.flat, "es").map((item) => <li key={item}>{item}</li>)}</ul>}
          </section>
        ))}
      </div>
    )}
    {category.steps && (
      <ol className="v2-menu-steps">
        {category.steps.map((step) => (
          <li key={step.k}>
            <span>{step.k}</span>
            <div><strong>{tr(step.t, "es")}</strong><p>{tr(step.v, "es")}</p></div>
          </li>
        ))}
      </ol>
    )}
  </div>
);

const Reservation = () => {
  const [form, setForm] = useState({ name: "", date: todayLocal(), hour: "11:00", people: "2", note: "" });
  const set = (key) => (event) => setForm((value) => ({ ...value, [key]: event.target.value }));
  const message = useMemo(() => [
    "¡Hola! Me gustaría reservar una mesa en Deseos.",
    "",
    `Nombre: ${form.name || "—"}`,
    `Fecha: ${form.date}`,
    `Hora: ${form.hour}`,
    `Personas: ${form.people}`,
    form.note ? `Nota: ${form.note}` : null,
  ].filter(Boolean).join("\n"), [form]);

  const submit = (event) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    window.open(`https://wa.me/${INFO.whatsapp}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="v2-reserva" className="v2-section v2-reservation">
      <div className="v2-reservation-copy">
        <span className="v2-kicker"><CalendarDays size={15} /> Reserva fácil</span>
        <h2>Tu mesa, lista cuando llegues.</h2>
        <p>Elige el momento. WhatsApp se abre con todo preparado y nosotros confirmamos la mesa.</p>
        <div className="v2-reservation-photo"><img src={IMG.terrace} alt="Entrada de Deseos" loading="lazy" /></div>
      </div>
      <form className="v2-form" onSubmit={submit}>
        <label>
          <span>Tu nombre</span>
          <input required value={form.name} onChange={set("name")} placeholder="¿Cómo te llamas?" autoComplete="name" />
        </label>
        <div className="v2-form-grid">
          <label><span><CalendarDays size={15} /> Día</span><input type="date" min={todayLocal()} value={form.date} onChange={set("date")} /></label>
          <label><span><Clock3 size={15} /> Hora</span><select value={form.hour} onChange={set("hour")}>{HOURS.map((hour) => <option key={hour}>{hour}</option>)}</select></label>
        </div>
        <label><span><Users size={15} /> Personas</span><select value={form.people} onChange={set("people")}>{PEOPLE.map((people) => <option key={people}>{people}</option>)}</select></label>
        <label><span>¿Algo que debamos saber?</span><input value={form.note} onChange={set("note")} placeholder="Terraza, carrito, alergias…" /></label>
        <button className="v2-primary v2-submit" type="submit"><MessageCircle size={18} /> Reservar por WhatsApp</button>
        <p className="v2-form-hint">No enviamos nada sin tu confirmación en WhatsApp.</p>
      </form>
    </section>
  );
};

export const V2App = () => {
  const [activeMenu, setActiveMenu] = useState(MENU[0].id);
  const category = MENU.find((item) => item.id === activeMenu) || MENU[0];

  return (
    <div className="v2-app">
      <header className="v2-topbar">
        <button className="v2-brand" onClick={() => go("v2-inicio")} aria-label="Ir al inicio">
          <SunLogo animate={false} className="v2-brand-mark" />
          <span><strong>Deseos</strong><small>Brunch & Coffee</small></span>
        </button>
        <div className="v2-top-actions">
          <span className="v2-open"><i /> Abierto hasta las 18:00</span>
          <LangSwitcher tone="light" />
          <button className="v2-top-reserve" onClick={() => go("v2-reserva")}>Reservar</button>
        </div>
      </header>

      <main>
        <section id="v2-inicio" className="v2-hero">
          <img src={IMG.hero} alt="La barra de Deseos por la mañana" />
          <div className="v2-hero-shade" />
          <motion.div className="v2-hero-content" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}>
            <span className="v2-kicker v2-kicker-light"><Sparkles size={15} /> Buenos días, Alicante</span>
            <h1>Tu mañana empieza aquí.</h1>
            <p>Café de especialidad, brunch al momento y una mesa al sol.</p>
            <div className="v2-hero-actions">
              <button className="v2-primary" onClick={() => go("v2-carta")}><Coffee size={18} /> Ver la carta</button>
              <button className="v2-secondary" onClick={() => go("v2-visita")}><Compass size={18} /> Cómo llegar</button>
            </div>
          </motion.div>
          <div className="v2-hero-info">
            <div><Clock3 size={18} /><span><small>Hoy</small>09:00 a 18:00</span></div>
            <div><MapPin size={18} /><span><small>Alicante</small>A un paseo de ti</span></div>
          </div>
        </section>

        <section className="v2-quick" aria-label="Accesos rápidos">
          <button onClick={() => go("v2-carta")}><Coffee /><span><strong>La carta</strong><small>Precios y sabores</small></span><ArrowRight /></button>
          <button onClick={() => go("v2-reserva")}><CalendarDays /><span><strong>Reservar</strong><small>En menos de un minuto</small></span><ArrowRight /></button>
          <a href={INFO.directions} target="_blank" rel="noopener noreferrer"><MapPin /><span><strong>Cómo llegar</strong><small>Ábrelo en Maps</small></span><ArrowRight /></a>
        </section>

        <section id="v2-favoritos" className="v2-section v2-favorites">
          <div className="v2-section-head">
            <div><span className="v2-kicker"><Heart size={15} /> Los más pedidos</span><h2>Elige con los ojos.</h2></div>
            <p>Cuatro favoritos que cuentan la historia de Deseos en un solo bocado.</p>
          </div>
          <div className="v2-favorite-rail">
            {FAVORITES.map((item) => (
              <article className="v2-dish" key={item.t}>
                <div><img src={item.image} alt={item.t} loading="lazy" /><span>{item.k}</span></div>
                <h3>{item.t}</h3><p>{item.d}</p><strong>{item.price}</strong>
              </article>
            ))}
          </div>
        </section>

        <section id="v2-carta" className="v2-section v2-menu">
          <div className="v2-section-head v2-section-head-light">
            <div><span className="v2-kicker v2-kicker-light"><Coffee size={15} /> La carta</span><h2>¿Qué te apetece hoy?</h2></div>
            <p>Todo se prepara al momento. Desliza las categorías y encuentra tu favorito.</p>
          </div>
          <div className="v2-menu-tabs" role="tablist" aria-label="Categorías de la carta">
            {MENU.map((item) => (
              <button key={item.id} role="tab" aria-selected={activeMenu === item.id} onClick={() => setActiveMenu(item.id)}>{tr(item.label, "es")}</button>
            ))}
          </div>
          <MenuPanel category={category} />
        </section>

        <section className="v2-story">
          <div className="v2-story-photo"><img src={IMG.interior} alt="Interior de Deseos" loading="lazy" /><span>Desde las 9:00</span></div>
          <div className="v2-story-copy"><span className="v2-kicker"><Sparkles size={15} /> Hecho aquí</span><h2>Sin prisas. Sin atajos.</h2><p>Granos seleccionados, pan del día e ingredientes frescos. Tu pedido empieza cuando lo haces tú.</p><button onClick={() => go("v2-carta")}>Explorar la carta <ArrowRight size={18} /></button></div>
        </section>

        <section id="v2-visita" className="v2-section v2-visit">
          <div className="v2-visit-photo"><img src={IMG.terrace} alt="Entrada de Deseos en Alicante" loading="lazy" /></div>
          <div className="v2-visit-card">
            <span className="v2-kicker"><MapPin size={15} /> Ven a vernos</span>
            <h2>Tu rincón de mañana en Alicante.</h2>
            <dl><div><dt>Dirección</dt><dd>{INFO.address}<br />{INFO.city}</dd></div><div><dt>Horario</dt><dd>Todos los días<br />09:00 a 18:00</dd></div></dl>
            <a className="v2-primary" href={INFO.directions} target="_blank" rel="noopener noreferrer"><Compass size={18} /> Abrir en Google Maps</a>
          </div>
        </section>

        <Reservation />
      </main>

      <footer className="v2-footer">
        <SunLogo animate={false} className="v2-footer-logo" />
        <p>Deseos Brunch & Coffee</p>
        <a href={INFO.instagram} target="_blank" rel="noopener noreferrer"><Instagram size={18} /> @deseos.brunch.coffee</a>
        <small>© {new Date().getFullYear()} Deseos · Realizado por <a href="https://sourati.digital" target="_blank" rel="noopener noreferrer">Sourati Digital</a></small>
      </footer>

      <nav className="v2-bottom-nav" aria-label="Navegación principal">
        <button onClick={() => go("v2-inicio")}><Home /><span>Inicio</span></button>
        <button onClick={() => go("v2-carta")}><Coffee /><span>Carta</span></button>
        <button className="v2-bottom-main" onClick={() => go("v2-reserva")}><CalendarDays /><span>Reservar</span></button>
        <button onClick={() => go("v2-visita")}><MapPin /><span>Visita</span></button>
      </nav>
    </div>
  );
};
